import { makeStyles, Theme } from '@material-ui/core';
import createStyles from '@material-ui/core/styles/createStyles';
import { colors } from '../../theme/tokens';
import { fontFamilies } from '../../theme/fonts';

/**
 * Styles shared by the auth screens (login, register, password recovery).
 *
 * Every color comes from the design tokens (`colors.*`, published as
 * `--ev-*` CSS variables) or from the MUI palette, so the screens follow the
 * light/dark mode automatically. Do not add color literals here — add a token
 * in `theme/tokens.ts` instead.
 */
export const useLoginStyles = makeStyles((theme: Theme) => {
  // Pill-shaped inputs and buttons, in every mode.
  const inputRadius = 999;

  return createStyles({
    // ── Page shell ───────────────────────────────────────────────────────
    // Hero card on the left, form column on the right (mirrors the Figma).
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(3),
      padding: theme.spacing(3),
      boxSizing: 'border-box',
      fontFamily: theme.typography.fontFamily,
      color: colors.textPrimary,
      // One shape for every mode; only the token colours differ per mode.
      background: `radial-gradient(38% 50% at 52% 50%, ${colors.authGlowCenter} 0%, transparent 100%), linear-gradient(90deg, ${colors.authGlowEdge} 0%, transparent 65%), ${colors.authBase}`,
      [theme.breakpoints.down('sm')]: {
        padding: 0,
        gap: 0,
      },
    },

    // ── Hero (photo) card ────────────────────────────────────────────────
    heroPanel: {
      flex: '1 1 50%',
      position: 'sticky',
      top: theme.spacing(3),
      height: `calc(100vh - ${theme.spacing(6)}px)`,
      minHeight: 420,
      overflow: 'hidden',
      borderRadius: theme.spacing(3),
      background: colors.heroGradient,
      // Glass edge: a 1px rim that catches light at the corners and fades along
      // the sides. Drawn as a masked gradient so it hugs the rounded corners.
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        borderRadius: 'inherit',
        padding: 1,
        background: `linear-gradient(135deg, ${colors.heroRimLight} 0%, ${colors.heroRimDim} 35%, ${colors.heroRimDim} 65%, ${colors.heroRimLight} 100%)`,
        WebkitMask:
          'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        pointerEvents: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    // Cut-out photo sits at the bottom of the gradient card (as in Figma).
    heroPhoto: {
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: '50% bottom',
    },
    heroOverlay: {
      position: 'absolute',
      inset: 0,
      background: colors.imageScrim,
    },
    heroBrand: {
      position: 'absolute',
      left: 'clamp(24px, 3.2vw, 48px)',
      right: 'clamp(24px, 3.2vw, 48px)',
      bottom: 'clamp(24px, 3.2vw, 48px)',
      color: colors.onImage,
    },
    heroLogo: {
      display: 'block',
      height: 'clamp(52px, 5.4vw, 80px)',
      marginBottom: theme.spacing(1.5),
    },
    heroTagline: {
      margin: 0,
      fontSize: 'clamp(0.9375rem, 1.1vw, 1.125rem)',
      fontWeight: 500,
      color: colors.onImage,
    },

    // ── Form column ──────────────────────────────────────────────────────
    formPanel: {
      flex: '1 1 50%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      alignSelf: 'stretch',
      padding: theme.spacing(4),
      [theme.breakpoints.down('sm')]: {
        flex: 1,
        padding: theme.spacing(4, 3),
      },
    },
    formInner: {
      width: '100%',
      maxWidth: 400,
    },
    formInnerWide: {
      width: '100%',
      maxWidth: 480,
    },

    // Logo above the form — only when the hero card is hidden (small screens)
    logoWrap: {
      marginBottom: theme.spacing(4),
      textAlign: 'center',
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    logoImg: {
      height: 48,
    },

    heading: {
      margin: '0 auto',
      fontFamily: fontFamilies.display,
      fontSize: 'clamp(1.5rem, 1.1rem + 1.2vw, 2.25rem)',
      lineHeight: 1.2,
      fontWeight: 700,
      letterSpacing: '-0.02em',
      textAlign: 'center',
      width: 'fit-content',
      background: colors.authHeading,
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    subheading: {
      margin: theme.spacing(0.5, 0, 3),
      fontSize: theme.typography.body2.fontSize,
      textAlign: 'center',
      color: colors.textSecondary,
    },
    formLabel: {
      margin: theme.spacing(2, 0, 1),
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 600,
      color: colors.textPrimary,
    },

    // ── Inputs (shared by login + register) ──────────────────────────────
    form: {
      width: '100%',
      // Browser autofill paints its own (white/yellow) fill — keep ours.
      '& input:-webkit-autofill, & input:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 100px ${colors.authInputBg} inset`,
        WebkitTextFillColor: colors.textPrimary,
        caretColor: colors.textPrimary,
        borderRadius: 'inherit',
      },
      // Autocomplete selects: drop MUI's extra padding so they match the
      // text inputs' height.
      '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
        padding: 0,
        paddingRight: theme.spacing(1),
      },
      '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input':
        {
          padding: '13px 14px',
        },
      '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
        color: colors.textMuted,
      },
      '& .MuiFormControl-marginNormal': {
        marginTop: theme.spacing(1.5),
        marginBottom: 0,
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: inputRadius,
        fontSize: theme.typography.body2.fontSize,
        backgroundColor: colors.authInputBg,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.borderStrong,
      },
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.textMuted,
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
      '& .MuiInputLabel-outlined': {
        fontSize: theme.typography.body2.fontSize,
        color: colors.textSecondary,
        transform: 'translate(14px, 14px) scale(1)',
      },
      // Label of a field with a leading icon rests after the icon.
      '& .ev-label-adorned:not(.MuiInputLabel-shrink)': {
        transform: 'translate(44px, 14px) scale(1)',
      },
      '& .MuiInputAdornment-root': {
        color: colors.textMuted,
      },
      '& .MuiInputLabel-outlined.Mui-focused': {
        color: theme.palette.primary.main,
      },
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.85)',
      },
      '& .MuiInputBase-input': {
        fontSize: theme.typography.body2.fontSize,
        padding: '13px 14px',
      },
      '& .MuiSelect-outlined.MuiSelect-outlined': {
        padding: '13px 14px',
      },
      '& .MuiSelect-icon, & .MuiIconButton-root': {
        color: colors.textMuted,
      },
      [theme.breakpoints.down('xs')]: {
        '& .MuiInputBase-input': {
          fontSize: 16,
        },
      },
      // Register: step tabs + section captions
      '& .MuiTab-root': {
        fontSize: theme.typography.caption.fontSize,
      },
      '& .MuiTypography-caption': {
        fontWeight: 700,
        color: colors.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      },
    },

    rememberRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: theme.spacing(1.5, 0, 2.5),
      '& .MuiFormControlLabel-root': {
        margin: 0,
      },
      '& .MuiFormControlLabel-label': {
        fontSize: theme.typography.caption.fontSize,
        color: colors.textMuted,
      },
      '& .MuiLink-root': {
        fontSize: theme.typography.caption.fontSize,
      },
    },
    // Rebrand checkbox: small rounded square, brand outline, brand tick.
    checkbox: {
      padding: theme.spacing(0, 1, 0, 0),
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    checkIcon: {
      display: 'block',
      width: 16,
      height: 16,
      boxSizing: 'border-box',
      borderRadius: 4,
      border: `2px solid ${theme.palette.primary.main}`,
    },
    checkIconChecked: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      boxSizing: 'border-box',
      borderRadius: 4,
      border: `2px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
    },

    // Primary action — the brand gradient comes from the theme's
    // `containedPrimary` override, so only layout is set here.
    submit: {
      marginTop: theme.spacing(1),
      marginBottom: 0,
      height: 44,
      padding: theme.spacing(0, 2),
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 600,
      borderRadius: 999,
      boxShadow: 'none',
      // Same orange-red → pink as the design (not the theme's pink → orange).
      background: colors.brandGradient,
      '&:hover': {
        boxShadow: 'none',
        background: colors.brandGradient,
        filter: 'brightness(0.92)',
      },
      '&.Mui-disabled, &:disabled': {
        // Only overriding color (not background) let MUI's own disabled
        // background win, which is a near-transparent grey — combined with
        // textMuted that made the button all but invisible while loading.
        background: colors.bgRaised,
        color: colors.textMuted,
      },
    },

    link: {
      color: colors.textSecondary,
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 500,
      textDecoration: 'none',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    linkAccent: {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    footerText: {
      marginTop: theme.spacing(3),
      textAlign: 'center',
      fontSize: theme.typography.body2.fontSize,
      color: colors.textSecondary,
    },

    notice: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1.25, 1.75),
      borderRadius: 10,
      background: colors.tintBlue,
      border: `1px solid ${colors.borderStrong}`,
      fontSize: theme.typography.body2.fontSize,
      color: colors.onBlue,
    },
    alert: {
      marginBottom: theme.spacing(2),
      borderRadius: 10,
      '&.MuiAlert-standardSuccess': {
        backgroundColor: colors.tintGreen,
        color: colors.onGreen,
        '& .MuiAlert-icon': {
          color: colors.onGreen,
        },
      },
    },

    // Used by the password-recovery pages, which still render a bare <main>.
    main: { display: 'block' },
    paper: { display: 'block' },
    avatar: { display: 'none' },
  });
});
